// Data Integrations API — thin wrappers over the shared axios instance.
// Kept out of ApiContext on purpose: these are used only by this tab, and ApiContext rebuilds its
// value object on every render (see the Jumio badge notes in CLAUDE.md).
import axios from 'utils/axios';
import type {
  ApiEnvelope,
  CatalogueField,
  DataSourceSummary,
  InboundDocumentDetail,
  InboundDocumentRow,
  InboundDocumentsStatus,
  IntegrationLookups,
  IntegrationProcess,
  PipelineDetail,
  PipelineSpec,
  PipelineSummary,
  RecordEvaluation,
  RunDetail,
  RunRow,
  SourceEntity,
  SourceRecordRow,
  TestRunResult
} from './types';

const base = (clientUid: string) => `data-integrations/client/${clientUid}`;
const unwrap = <T>(p: Promise<{ data: ApiEnvelope<T> }>) => p.then((r) => r.data);

export const diApi = {
  getSources: (c: string) => unwrap<DataSourceSummary[]>(axios.get(`${base(c)}/sources`)),
  stopSource: (c: string, s: string) => unwrap<DataSourceSummary>(axios.post(`${base(c)}/sources/${s}/stop`)),
  resumeSource: (c: string, s: string) => unwrap<DataSourceSummary>(axios.post(`${base(c)}/sources/${s}/resume`)),
  getCatalogue: (c: string, s: string, entity: SourceEntity = 'loads') =>
    unwrap<CatalogueField[]>(axios.get(`${base(c)}/sources/${s}/catalogue`, { params: { entity } })),
  getRecords: (c: string, s: string, params: { entity?: SourceEntity; search?: string; limit?: number } = {}) =>
    unwrap<SourceRecordRow[]>(axios.get(`${base(c)}/sources/${s}/records`, { params })),
  getProcesses: (c: string) => unwrap<IntegrationProcess[]>(axios.get(`${base(c)}/processes`)),
  getLookups: (c: string) => unwrap<IntegrationLookups>(axios.get(`${base(c)}/lookups`)),
  getPipelines: (c: string) => unwrap<PipelineSummary[]>(axios.get(`${base(c)}/pipelines`)),
  createPipeline: (c: string, body: { name: string; sourceUid: string; entity: SourceEntity; template?: 'blank' | 'aljex_high_value' }) =>
    unwrap<PipelineDetail>(axios.post(`${base(c)}/pipelines`, body)),
  getPipeline: (c: string, p: string) => unwrap<PipelineDetail>(axios.get(`${base(c)}/pipelines/${p}`)),
  saveDraft: (c: string, p: string, body: { spec: PipelineSpec; name?: string }) =>
    unwrap<PipelineDetail>(axios.put(`${base(c)}/pipelines/${p}/draft`, body)),
  preview: (c: string, p: string, body: { recordId: string; spec?: PipelineSpec }) =>
    unwrap<RecordEvaluation>(axios.post(`${base(c)}/pipelines/${p}/preview`, body)),
  test: (c: string, p: string, body: { days?: number } = {}) => unwrap<TestRunResult>(axios.post(`${base(c)}/pipelines/${p}/test`, body)),
  publish: (c: string, p: string, body: { mode: 'SHADOW' | 'LIVE'; note?: string; backfillDays?: number }) =>
    unwrap<PipelineDetail>(axios.post(`${base(c)}/pipelines/${p}/publish`, body)),
  pause: (c: string, p: string) => unwrap<PipelineDetail>(axios.post(`${base(c)}/pipelines/${p}/pause`)),
  resume: (c: string, p: string) => unwrap<PipelineDetail>(axios.post(`${base(c)}/pipelines/${p}/resume`)),
  remove: (c: string, p: string) => unwrap<{ uid: string }>(axios.delete(`${base(c)}/pipelines/${p}`)),
  getRuns: (c: string, params: { pipelineUid?: string; outcome?: string; page?: number; limit?: number } = {}) =>
    unwrap<{ rows: RunRow[]; total: number }>(axios.get(`${base(c)}/runs`, { params })),
  getRun: (c: string, r: string) => unwrap<RunDetail>(axios.get(`${base(c)}/runs/${r}`)),
  retryRun: (c: string, r: string) => unwrap<RunRow>(axios.post(`${base(c)}/runs/${r}/retry`)),
  // SFTP documents — read-only; the prod Lambda writes them.
  getDocumentsStatus: (c: string) => unwrap<InboundDocumentsStatus>(axios.get(`${base(c)}/documents/status`)),
  getDocuments: (c: string, params: { search?: string; status?: string; page?: number; limit?: number } = {}) =>
    unwrap<{ rows: InboundDocumentRow[]; total: number; hasMore: boolean }>(axios.get(`${base(c)}/documents`, { params })),
  getDocument: (c: string, pro: string) => unwrap<InboundDocumentDetail>(axios.get(`${base(c)}/documents/${encodeURIComponent(pro)}`)),
  // Ask the Lambda to run now — it works asynchronously, results appear a little later.
  requestDocumentsScan: (c: string) => unwrap<{ requested: string; requestedAt: string }>(axios.post(`${base(c)}/documents/scan`)),
  requestDocumentReread: (c: string, pro: string) =>
    unwrap<{ requested: string; pro: string; requestedAt: string }>(axios.post(`${base(c)}/documents/${encodeURIComponent(pro)}/reprocess`))
};
